// Persistent send-outbox shared by every chat-input surface (InputBar,
// MultiAgent slots). Extracted from InputBar's inline implementation
// (commit 8db7ba8) so MultiAgent gets the same page-reload-surviving
// delivery guarantee.
//
// Semantics:
//   - enqueueAndSend(text) writes the message to localStorage FIRST,
//     then attempts the POST. Success removes it; failure keeps it with
//     attempts+1 and lastError.
//   - A retry loop flushes pending items every 8 s while online, plus
//     immediately when the browser fires the `online` event.
//   - Items are keyed by sessionName; each hook instance only retries
//     its own session's items. Items for other sessions stay parked
//     until a hook for that session mounts.
//   - Storage is shared across hook instances via the common key, so
//     InputBar and a MultiAgent slot for the same session cooperate
//     instead of double-sending (flushItem removes by unique id).

import { useState, useCallback, useEffect } from 'react';
import { api } from '../api/client';

export interface OutboxItem {
  id: string;
  sessionName: string;
  text: string;
  ts: number;
  attempts: number;
  lastError?: string;
}

const OUTBOX_KEY = 'cloud-outbox';

function loadOutbox(): OutboxItem[] {
  try { return JSON.parse(localStorage.getItem(OUTBOX_KEY) || '[]'); }
  catch { return []; }
}
function saveOutbox(items: OutboxItem[]) {
  try { localStorage.setItem(OUTBOX_KEY, JSON.stringify(items)); }
  catch { /* quota — drop silently */ }
}

// Module-level in-flight guard: when InputBar AND a MultiAgent slot for
// the same session are mounted, both run retry loops over the same
// storage. Without this, one item could POST twice (both loops grab it
// before either removes it). Module scope = shared across all hook
// instances in the tab.
const inFlight = new Set<string>();

export function useOutbox(sessionName: string) {
  const [outbox, setOutbox] = useState<OutboxItem[]>(() => loadOutbox());
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  const flushItem = useCallback(async (item: OutboxItem): Promise<boolean> => {
    if (inFlight.has(item.id)) return false;
    inFlight.add(item.id);
    try {
      await api.sessions.sendInput(item.sessionName, item.text);
      setOutbox(() => {
        // Re-read storage so concurrent hook instances' mutations are
        // respected (last-write-wins on the same tab is fine — all
        // instances share the event loop).
        const next = loadOutbox().filter((x) => x.id !== item.id);
        saveOutbox(next);
        return next;
      });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setOutbox(() => {
        const next = loadOutbox().map((x) =>
          x.id === item.id
            ? { ...x, attempts: x.attempts + 1, lastError: msg.slice(0, 120) }
            : x,
        );
        saveOutbox(next);
        return next;
      });
      return false;
    } finally {
      inFlight.delete(item.id);
    }
  }, []);

  const enqueueAndSend = useCallback((text: string): OutboxItem => {
    const item: OutboxItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sessionName,
      text,
      ts: Date.now(),
      attempts: 0,
    };
    setOutbox(() => {
      const next = [...loadOutbox(), item];
      saveOutbox(next);
      return next;
    });
    void flushItem(item);
    return item;
  }, [sessionName, flushItem]);

  // Online/offline tracking — the `online` event is the fast retry path.
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Retry loop: every 8 s while online, immediate on online-flip. Only
  // retries THIS session's items; stops at the first failure per tick
  // to avoid hammering a downed backend.
  useEffect(() => {
    if (!isOnline) return;
    let cancelled = false;
    const tick = async () => {
      const pending = loadOutbox().filter((x) => x.sessionName === sessionName);
      for (const item of pending) {
        if (cancelled) return;
        const ok = await flushItem(item);
        if (!ok) break;
      }
    };
    void tick();
    const id = setInterval(tick, 8_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isOnline, sessionName, flushItem]);

  const discardSession = useCallback(() => {
    setOutbox(() => {
      const next = loadOutbox().filter((x) => x.sessionName !== sessionName);
      saveOutbox(next);
      return next;
    });
  }, [sessionName]);

  const sessionOutbox = outbox.filter((x) => x.sessionName === sessionName);

  return {
    /** Items pending for this session (un-flushed or retrying). */
    sessionOutbox,
    /** True while the browser reports network connectivity. */
    isOnline,
    /** Persist + attempt to send. Returns the created item. */
    enqueueAndSend,
    /** Manually re-attempt one item now (banner retry button). */
    flushItem,
    /** Drop all of this session's parked items. */
    discardSession,
  };
}
